import { INTERNAL_EVENTS } from '@/const/events';
import { getLQDefinition } from '@/cookies/laquesis/lqstatus';
import { ninjaBus } from '@/eventbus';
import { getLogger } from '@/logger';
import { nativeHandlerFn } from '@/ninja/native/handler';
import { state } from '@/ninja/state';

const logger = getLogger('laquesis/setupWindow');
/**
 * Expose Laquesis functions to the window object
 */
export function setupWindow() {
  window.isVariantEnabled = function (testName: string, variantName: string, queueImpression?: boolean) {
    return isVariantEnabled(testName, variantName, queueImpression);
  };
  window.getLaquesisVariant = function (testName: string, queueImpression?: boolean) {
    return getLaquesisVariant(testName, queueImpression);
  };
  window.flushImpressions = function () {
    flushImpressions();
  };

  window.isFeatureEnabled = function (flagName) {
    return isFeatureEnabled(flagName);
  };
  window.laquesisSetUserId = function (id) {
    laquesisSetUserId(id);
  };
  window.laquesisDropUserId = function () {
    laquesisDropUserId();
  };
  window.laquesisSetUserUUId = function (id) {
    laquesisSetUserUUId(id);
  };
  window.laquesisDropUserUUId = function () {
    laquesisDropUserUUId();
  };
  window.laquesisGetActive = function () {
    const definition = getLQDefinition();

    return {
      experiments: Object.fromEntries(state.activeExps),
      flags: Array.from(state.activeFlags),
      surveys: {
        assigned: state.activeSurveys.map(survey => Number(survey.id)),
        shown: definition.showedSurveys.map(Number),
      },
    };
  };
  ninjaBus.emit(INTERNAL_EVENTS.SETUP_SURVEYS_WINDOW, '');
}

/**
 * Expose Laquesis functions to the window object when using webviews (in Android or iOS)
 */
export function setupNativeWindow() {
  window.getLaquesisVariant = function (testName) {
    return nativeHandlerFn('getLaquesisVariant', testName) as string;
  };
  window.isVariantEnabled = function (testName, variantName) {
    if (window.getLaquesisVariant) {
      const variant = window.getLaquesisVariant(testName);
      return variant === variantName;
    }
    return false;
  };
  window.isFeatureEnabled = function (flagName) {
    return nativeHandlerFn('isFeatureEnabled', flagName) as boolean;
  };
  window.flushImpressions = function () {
    // nothing is expected. All calls are proxied to the host and apps do not have a concept of delayed tracking
  };
}

function flushImpressions() {
  for (const [exp, callback] of state.impressionsQueue.entries()) {
    try {
      logger.debug(`Flushing delayed impression callback for ${exp}`);
      callback();
    } catch (e) {
      logger.error('Error while flushing impressions', e);
    }
  }

  state.impressionsQueue.clear();
}

function isVariantEnabled(testName: string, variant: string, queueImpression?: boolean) {
  const expName = testName.toLowerCase();
  const variantName = variant.toLowerCase();

  if (!navigator.cookieEnabled) {
    return false;
  }
  const assignedVariant = state.activeExps.get(expName);
  if (assignedVariant !== variantName) {
    return false;
  }

  const impressionFn = () => {
    ninjaBus.emit(INTERNAL_EVENTS.TRACK_IMPRESSION, '', { expName, variantName });
  };

  if (queueImpression) {
    state.impressionsQueue.set(`${expName}@${variantName}`, impressionFn);
    logger.debug(`isVariantEnabled: queued impression for ${expName} ${variantName}`);
  } else {
    impressionFn();
  }

  return true;
}

function getLaquesisVariant(testName: string, queueImpression?: boolean) {
  if (!navigator.cookieEnabled) {
    return null;
  }

  const expName = testName.toLowerCase();
  const variantName = state.activeExps.get(expName);
  if (!variantName) {
    return null;
  }

  const impressionFn = () => {
    ninjaBus.emit(INTERNAL_EVENTS.TRACK_IMPRESSION, '', { expName, variantName });
  };

  if (queueImpression) {
    state.impressionsQueue.set(`${expName}@${variantName}`, impressionFn);
    logger.debug(`getLaquesisVariant: queued impression for ${expName} ${variantName}`);
  } else {
    impressionFn();
  }

  return variantName;
}

function isFeatureEnabled(flagName: string) {
  if (!navigator.cookieEnabled) {
    return false;
  }
  return state.activeFlags.has(flagName.toLowerCase());
}

function laquesisSetUserId(id: number | string) {
  if (id && state.userId !== id) {
    state.userId = id;
    ninjaBus.emit(INTERNAL_EVENTS.DEFINITION_RELOAD, '');
  }
}

function laquesisDropUserId() {
  if (state.userId) {
    state.userId = undefined;
    ninjaBus.emit(INTERNAL_EVENTS.DEFINITION_RELOAD, '');
  }
}

function laquesisSetUserUUId(id: number | string) {
  if (id && state.userUUId !== id) {
    state.userUUId = id;
    ninjaBus.emit(INTERNAL_EVENTS.DEFINITION_RELOAD, '');
  }
}

function laquesisDropUserUUId() {
  if (state.userUUId) {
    state.userUUId = undefined;

    ninjaBus.emit(INTERNAL_EVENTS.DEFINITION_RELOAD, '');
  }
}
